import { authenticationReducerActions, myOrganizationReducerActions } from "../../../_services/store";
import { userService } from "../user.service";
import { history } from "../../../_services/history";
import { buildNavigationUpdateAction } from "../../../_services/store/navigation.utils";
import useAcceptedDocuments from "../../../_hooks/useAcceptedDocuments";
import { UserRole, UserType } from "../../../_statics/UserStatics";
import { EnvironmentEnum, getEnvironment } from "../../../EnvironmentUtils";
import { updateLastLogin } from "./user.actions";
import { GoogleAuthProvider, OAuthProvider, signInWithEmailAndPassword, signInWithPopup, signInWithCredential, signOut, TotpMultiFactorGenerator, getMultiFactorResolver } from 'firebase/auth';
import { prepareAxiosAuth } from "../../../_services/axios-helper";
import { clearSession } from "../../../_services/session";
import { trackExistingCustomerLogin } from "../../../_services/metaTracking";
import { auth } from "../../../firebase";
import { setupActions } from "./setup.actions";

export const login = (username, password, selectedOrganization, redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequest());
        try {
            const user = await userService.login(username, password, selectedOrganization);

            // Only load cello in prod
            if (EnvironmentEnum.APP === getEnvironment() && user?.platformUserType !== UserType.TAXCONSULTANT) {
                Promise.resolve().then(() => userService.getCelloToken());
            }
            dispatch(authenticationReducerActions.loginSuccess(user));
            trackExistingCustomerLogin(user);

            const { activeTosVersionAccepted, activeDpaVersionAccepted } = useAcceptedDocuments(user);

            if (!activeTosVersionAccepted || !activeDpaVersionAccepted) {
                // The documents have to be accepted before we can continue.
                return;
            }

            const organizationUUID =
                user?.selectedOrganization?.externalUUID ||
                (user?.availableOrganizations?.length === 1 ? user.availableOrganizations[0].externalUUID : null);

            if (!organizationUUID) {
                // Several organizations and none selected yet - the user has to choose one.
                return;
            }

            await dispatch(selectOrganizationByLogin(organizationUUID, redirectUrl, null));
            dispatch(updateLastLogin());
            if (!user.needsEmailCodeVerification) {
                history.replace(redirectUrl || "/");
            }
        } catch (error) {
            if (error.message) {
                dispatch(authenticationReducerActions.loginFailure(error.message));
            } else if (error.errorMessage) {
                dispatch(authenticationReducerActions.loginFailure(error.errorMessage));
            } else {
                dispatch(authenticationReducerActions.loginFailure(error));
            }
        }
    };
};


export const resumeLoginWithSSO = (user, redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequest(user));

        try {
            // Put the user back into the state first, so the login form can render the
            // organization selection if we cannot resolve an organization on our own.
            dispatch(authenticationReducerActions.loginSuccess(user));

            const organizationUUID =
                user?.selectedOrganization?.externalUUID ||
                (user?.availableOrganizations?.length === 1 ? user.availableOrganizations[0].externalUUID : null);

            if (!organizationUUID) {
                // Several organizations and none selected yet - the user has to choose one.
                return;
            }

            await dispatch(selectOrganizationByLogin(organizationUUID, redirectUrl, null));
            dispatch(updateLastLogin());
            if (!user.needsEmailCodeVerification) {
                history.replace(redirectUrl || "/");
            }
        } catch (error) {
            if (error.message) {
                dispatch(authenticationReducerActions.loginFailure(error.message));
            } else if (error.errorMessage) {
                dispatch(authenticationReducerActions.loginFailure(error.errorMessage));
            } else {
                dispatch(authenticationReducerActions.loginFailure(error));
            }
        }
    };
};



export const googleLogin = (redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequestWithSSO("SSO"));
        try {
            const provider = new GoogleAuthProvider();
            auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

            // Step 1: Trigger Google sign-in popup
            const result = await signInWithPopup(auth, provider);

            // Step 2: Get the Firebase ID token from the signed-in user
            const firebaseIdToken = await result.user.getIdToken();
            
            // Get the expiration time from the token claims
            const tokenResult = await result.user.getIdTokenResult();
            const expirationTime = tokenResult.expirationTime;

            let tokenUser = {
                access_token: firebaseIdToken,
                expiration_time: expirationTime,
                refresh_token: result.user.refreshToken
            }
            
            dispatch(processLogin(tokenUser, redirectUrl));
        } catch (error) {
            // Handle MFA required error
            if (error.code === 'auth/multi-factor-auth-required') {
                // Extract the resolver from the error using Firebase method
                const resolver = getMultiFactorResolver(auth, error);
                window.__mfaResolver = resolver;

                console.log('Google MFA - Stored resolver:', !!window.__mfaResolver);
                console.log('Resolver hints:', resolver?.hints);

                dispatch(authenticationReducerActions.mfaRequired({
                    resolverExists: true,
                    hints: resolver?.hints || []
                }));

            } else if (error.code === "auth/account-exists-with-different-credential") {
                dispatch(authenticationReducerActions.loginFailure("Für diese E-Mail-Adresse existiert bereits ein Konto mit einer anderen Anmeldemethode (z.B. Benutzername/Passwort oder ein anderer SSO-Anbieter). Bitte melde dich zuerst mit deiner bisherigen Methode an und wechsle dann den SSO-Anbieter in deinem Profil."));
            }  else if (error.code === 'auth/wrong-password') {
                dispatch(authenticationReducerActions.loginFailure("Das eingegebene Passwort ist falsch. Falls dein Konto über Google oder Microsoft erstellt wurde, verwende bitte den entsprechenden Login-Button."));
            } else if (error.code === 'auth/user-not-found') {
                dispatch(authenticationReducerActions.loginFailure("Kein Benutzer mit dieser E-Mail-Adresse gefunden."));
            } else if (error.code === 'auth/invalid-email') {
                dispatch(authenticationReducerActions.loginFailure("Die E-Mail-Adresse ist ungültig."));
            } else if (error.code === 'auth/too-many-requests') {
                dispatch(authenticationReducerActions.loginFailure("Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuche es später erneut."));
            } else if (error.code === 'auth/user-disabled') {
                dispatch(authenticationReducerActions.loginFailure("Dieser Benutzeraccount wurde deaktiviert."));
            } else if (error.message) {
                console.log('LoginV2 Error Message:', error.message);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else if (error.errorMessage) {
                console.log('LoginV2 Error Message:', error.errorMessage);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else {
                console.log('LoginV2 Error Message:', error);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            }
        }
    }
};

export const microsoftLogin = (redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequestWithSSO("SSO"));
        try {
            const provider = new OAuthProvider('microsoft.com');
            provider.addScope('openid');
            provider.addScope('profile');
            provider.addScope('email');
            auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

            // Step 1: Trigger Microsoft sign-in popup
            const result = await signInWithPopup(auth, provider);

            // Step 2: Get the Firebase ID token from the signed-in user
            const firebaseIdToken = await result.user.getIdToken();
            
            // Get the expiration time from the token claims
            const tokenResult = await result.user.getIdTokenResult();
            const expirationTime = tokenResult.expirationTime;

            let tokenUser = {
                access_token: firebaseIdToken,
                expiration_time: expirationTime,
                refresh_token: result.user.refreshToken
            }

            // Step 3: Proceed with your application's login logic
            dispatch(processLogin(tokenUser, redirectUrl));
        } catch (error) {
             if (error.code !== 'auth/multi-factor-auth-required') {
                dispatch(authenticationReducerActions.loginClearSSOMethod());
             }

            if (error.code === "auth/account-exists-with-different-credential") {
                // Automatically handle Microsoft provider linking:
                // 1. Extract credential from the failed sign-in attempt
                // 2. Call backend to delete old Firebase user and set provider to MICROSOFT
                // 3. Retry sign-in with the same credential (no second popup)
                const credential = OAuthProvider.credentialFromError(error);
                const idToken = credential?.idToken;

                if (credential && idToken) {
                    try {
                        await prepareAxiosAuth().post("/platform/user/public/link-microsoft-provider", {
                            microsoftIdToken: idToken
                        });

                        // Retry sign-in — old Firebase user was deleted, so Microsoft can create a new one
                        auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;
                        const retryResult = await signInWithCredential(auth, credential);

                        const firebaseIdToken = await retryResult.user.getIdToken();
                        const tokenResult = await retryResult.user.getIdTokenResult();

                        let tokenUser = {
                            access_token: firebaseIdToken,
                            expiration_time: tokenResult.expirationTime,
                            refresh_token: retryResult.user.refreshToken
                        };

                        dispatch(processLogin(tokenUser, redirectUrl));
                        return;
                    } catch (linkError) {
                        console.error('Microsoft provider linking failed:', linkError);
                        dispatch(authenticationReducerActions.loginFailure(
                            "Beim Verknüpfen des Microsoft-Kontos ist ein Fehler aufgetreten. Bitte versuche es erneut."
                        ));
                        return;
                    }
                } else {
                    dispatch(authenticationReducerActions.loginFailure(
                        "Für diese E-Mail-Adresse existiert bereits ein Konto mit einer anderen Anmeldemethode. Bitte melde dich zuerst mit deiner bisherigen Methode an und wechsle dann den SSO-Anbieter in deinem Profil."
                    ));
                }
            }
            else if (error.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, error);
                window.__mfaResolver = resolver;                
                dispatch(authenticationReducerActions.mfaRequired({
                    resolverExists: true,
                    hints: resolver?.hints || []
                }));

            }  else if (error.code === 'auth/wrong-password') {
                dispatch(authenticationReducerActions.loginFailure("Das eingegebene Passwort ist falsch. Falls dein Konto über Google oder Microsoft erstellt wurde, verwende bitte den entsprechenden Login-Button."));
            } else if (error.code === 'auth/user-not-found') {
                dispatch(authenticationReducerActions.loginFailure("Kein Benutzer mit dieser E-Mail-Adresse gefunden."));
            } else if (error.code === 'auth/invalid-email') {
                dispatch(authenticationReducerActions.loginFailure("Die E-Mail-Adresse ist ungültig."));
            } else if (error.code === 'auth/too-many-requests') {
                dispatch(authenticationReducerActions.loginFailure("Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuche es später erneut."));
            } else if (error.code === 'auth/user-disabled') {
                dispatch(authenticationReducerActions.loginFailure("Dieser Benutzeraccount wurde deaktiviert."));
            } else if (error.message) {
                console.log('LoginV2 Error Message:', error.message);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else if (error.errorMessage) {
                console.log('LoginV2 Error Message:', error.errorMessage);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else {
                console.log('LoginV2 Error Message:', error);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            }
        }
    }
};

export const datevLogin = (redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequestWithSSO("SSO"));
        try {
            const provider = new OAuthProvider('oidc.login-with-datev');
            provider.addScope('openid');      // REQUIRED by OIDC standard
            provider.addScope('account_id');  // REQUIRED by DATEV
            provider.addScope('profile');     // REQUIRED by DATEV
            provider.addScope('email');       // Highly recommended
            provider.setCustomParameters({
                enableWindowsSso: 'true' // This is required by DATEV
            });
            
            auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

            const result = await signInWithPopup(auth, provider);
            
            console.log('result', result);  
            // The user is successfully signed into Firebase at this point.
            const user = result.user;
            
            const firebaseIdToken = await user.getIdToken();
            
            // Get the expiration time from the token claims
            const tokenResult = await user.getIdTokenResult();
            const expirationTime = tokenResult.expirationTime;

            let tokenUser = {
                access_token: firebaseIdToken,
                expiration_time: expirationTime,
                refresh_token: user.refreshToken
            }
            
            // Step 3: Proceed with your application's login logic
            dispatch(processLogin(tokenUser, redirectUrl));
        } catch (error) {
            dispatch(authenticationReducerActions.loginClearSSOMethod());

            console.error('DATEV login error:', error);
            
            // Handle specific DATEV/Firebase errors with user-friendly messages
            let userFriendlyMessage;
            
            if (error.code === 'auth/multi-factor-auth-required') {
                // Extract the resolver from the error using Firebase method
                const resolver = getMultiFactorResolver(auth, error);
                window.__mfaResolver = resolver;
                
                console.log('DATEV MFA - Stored resolver:', !!window.__mfaResolver);
                
                dispatch(authenticationReducerActions.mfaRequired({
                    resolverExists: true,
                    hints: resolver?.hints || []
                }));
            } else if (error.code === 'auth/popup-closed-by-user') {
                userFriendlyMessage = "Anfrage-Timeout erreicht. Du kannst die DATEV-Anmeldung jedoch weiterhin im Pop-up-Fenster fortsetzen.";
            } else if (error.code === 'auth/popup-blocked') {
                userFriendlyMessage = "Pop-up wurde blockiert. Bitte erlaube Pop-ups für diese Seite und versuche es erneut.";
            } else if (error.code === 'auth/cancelled-popup-request') {
                userFriendlyMessage = "Die Anmeldung wurde abgebrochen. Bitte versuche es erneut.";
            } else if (error.code === 'auth/timeout') {
                userFriendlyMessage = "Die DATEV-Anmeldung dauert zu lange. Bitte überprüfe deine Internetverbindung und versuche es erneut.";
            } else if (error.code === 'auth/network-request-failed') {
                userFriendlyMessage = "Netzwerkfehler bei der DATEV-Anmeldung. Bitte überprüfe deine Internetverbindung und versuche es erneut.";
            } else if (error.code === 'auth/internal-error') {
                userFriendlyMessage = "Ein technisches Problem ist aufgetreten. Bitte versuche es später erneut oder kontaktiere den Support.";
            } else if (error.message && (error.message.includes('schema validation failed') || error.message.includes('Request schema validation failed'))) {
                userFriendlyMessage = "DATEV-Konfigurationsfehler. Bitte versuche es erneut oder verwende eine alternative Anmeldemethode.";
            } else if (error.message && error.message.includes('timeout')) {
                userFriendlyMessage = "Die DATEV-Anmeldung dauert zu lange. Bitte versuche es erneut.";
            } else if (error.message && error.message.includes('closed')) {
                userFriendlyMessage = "Die DATEV-Anmeldung wurde unterbrochen. Bitte versuche es erneut.";
            } else if (error.message && error.message.includes('network')) {
                userFriendlyMessage = "Netzwerkproblem bei der DATEV-Anmeldung. Bitte überprüfe deine Internetverbindung.";
            } else {
                // Generic fallback for any other DATEV-related errors
                userFriendlyMessage = "Bei der DATEV-Anmeldung ist ein Problem aufgetreten. Bitte versuche es erneut oder verwende eine alternative Anmeldemethode.";
            }
            
            dispatch(authenticationReducerActions.loginFailure(userFriendlyMessage));
        }
    }
};


export const processLogin = (accessToken, redirectUrl) => {
    return async (dispatch) => {
        try {
            const user = await userService.processOAuthToken(accessToken)
            
            // Only load cello in prod
            if (EnvironmentEnum.APP === getEnvironment() && user?.platformUserType !== UserType.TAXCONSULTANT) {
                Promise.resolve().then(() => userService.getCelloToken());
            }
            dispatch(authenticationReducerActions.loginSuccess(user));
            trackExistingCustomerLogin(user);

            const { activeTosVersionAccepted, activeDpaVersionAccepted } = useAcceptedDocuments(user);
            if (user.availableOrganizations && user.availableOrganizations.length === 1 && activeTosVersionAccepted &&
                activeDpaVersionAccepted) {
                await dispatch(selectOrganizationByLogin(user.availableOrganizations[0].externalUUID, redirectUrl, null));
                 dispatch(updateLastLogin());
                if (!user.needsEmailCodeVerification) {
                    history.replace(redirectUrl || "/");
                }
            }
            if (
                redirectUrl &&
                user &&
                activeTosVersionAccepted &&
                activeDpaVersionAccepted &&
                (user.selectedOrganization)
            ) {
                dispatch(updateLastLogin());
                if (!user.needsEmailCodeVerification) {
                    history.replace(redirectUrl);
                }
            }
        } catch (error) {
            if (error === "Request failed with status code 404"){
              dispatch(authenticationReducerActions.loginFailure("Benutzer wurde nicht gefunden. Bitte melde dich erneut an oder kontaktiere den Support."));
            } else if (error === "Request failed with status code 403"){
              dispatch(authenticationReducerActions.loginFailure("Dein Konto ist nicht für die Anmeldung mit SSO eingerichtet. Bitte melde dich mit deinem Benutzernamen und Passwort an oder kontaktiere deinen Administrator."));
            } else if (error.response) {
                const status = error.response.status;
                if (status === 404) {
                    dispatch(authenticationReducerActions.loginFailure("Benutzer wurde nicht gefunden. Bitte melde dich erneut an oder kontaktiere den Support."));
                } else if (status === 401) {
                    dispatch(authenticationReducerActions.loginFailure("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an."));
                } else if (status === 403) {
                    dispatch(authenticationReducerActions.loginFailure("Du hast keine Berechtigung für diese Aktion. Bitte kontaktiere deinen Administrator."));
                } else if (status === 500 || status === 502 || status === 503) {
                    dispatch(authenticationReducerActions.loginFailure("Ein Serverfehler ist aufgetreten. Bitte versuche es später erneut."));
                } else {
                    dispatch(authenticationReducerActions.loginFailure("Beim Laden der Benutzerdaten ist ein Fehler aufgetreten. Bitte versuche es später erneut."));
                }
            } else if (error.message) {
                dispatch(authenticationReducerActions.loginFailure(error.message));
            } else if (error.errorMessage) {
                dispatch(authenticationReducerActions.loginFailure(error.errorMessage));
            } else {
                dispatch(authenticationReducerActions.loginFailure(error));
            }
        }
    };
};

export const loginV2 = (username, password, selectedOrganization, redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequest());
        try {
            auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

            const firebaseUser = await signInWithEmailAndPassword(auth, username, password);
            const accessToken = await firebaseUser.user.getIdToken();
            
            // Get the expiration time from the token claims
            const tokenResult = await firebaseUser.user.getIdTokenResult();
            const expirationTime = tokenResult.expirationTime;
            
            let tokenUser = {
                access_token: accessToken,
                expiration_time: expirationTime,
                refresh_token: firebaseUser.user.refreshToken
            }
            
            await dispatch(processLogin(tokenUser, redirectUrl));
        } catch (error) {
            // Handle MFA required error
            if (error.code === 'auth/multi-factor-auth-required') {
                // Extract the resolver from the error using Firebase method
                const resolver = getMultiFactorResolver(auth, error);
                window.__mfaResolver = resolver;
    
                dispatch(authenticationReducerActions.mfaRequired({
                    resolverExists: true,
                    hints: resolver?.hints || []
                }));
            } else if (error.code === 'auth/wrong-password') {
                dispatch(authenticationReducerActions.loginFailure("Das eingegebene Passwort ist falsch. Falls dein Konto über Google oder Microsoft erstellt wurde, verwende bitte den entsprechenden Login-Button."));
            } else if (error.code === 'auth/user-not-found') {
                dispatch(authenticationReducerActions.loginFailure("Kein Benutzer mit dieser E-Mail-Adresse gefunden."));
            } else if (error.code === 'auth/invalid-email') {
                dispatch(authenticationReducerActions.loginFailure("Die E-Mail-Adresse ist ungültig."));
            } else if (error.code === 'auth/too-many-requests') {
                dispatch(authenticationReducerActions.loginFailure("Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuche es später erneut."));
            } else if (error.code === 'auth/user-disabled') {
                dispatch(authenticationReducerActions.loginFailure("Dieser Benutzeraccount wurde deaktiviert."));
            } else if (error.message) {
                console.log('LoginV2 Error Message:', error.message);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else if (error.errorMessage) {
                console.log('LoginV2 Error Message:', error.errorMessage);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            } else {
                console.log('LoginV2 Error Message:', error);
                dispatch(authenticationReducerActions.loginFailure("Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."));
            }
        }
    };
};

export const resolveMFA = (verificationCode) => {
    return async (dispatch) => {
        try {
            
            // Get the resolver from window (stored during MFA error)
            const resolver = window.__mfaResolver;
            console.log('Retrieved MFA resolver:', resolver);
            
            // Check if resolver and hints exist
            if (!resolver) {
                throw new Error('No MFA resolver available - please try logging in again');
            }
            
            if (!resolver.hints || resolver.hints.length === 0) {
                throw new Error('No MFA hints available - please try logging in again');
            }
            
            // Create TOTP assertion for the verification code
            const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
                resolver.hints[0].uid,
                verificationCode
            );
            
            // Resolve the MFA challenge
            const result = await resolver.resolveSignIn(multiFactorAssertion);
            
            // Continue with normal login flow
            const firebaseIdToken = await result.user.getIdToken();
            const tokenResult = await result.user.getIdTokenResult();
            const expirationTime = tokenResult.expirationTime;
            
            let tokenUser = {
                access_token: firebaseIdToken,
                expiration_time: expirationTime,
                refresh_token: result.user.refreshToken
            };
            
            // Clear MFA state and proceed with login
            dispatch(authenticationReducerActions.mfaResolved());
            dispatch(processLogin(tokenUser));
            
        } catch (error) {
            console.error('MFA resolution error:', error);
            if (error.code === 'auth/invalid-verification-code') {
                dispatch(authenticationReducerActions.loginFailure("Der eingegebene Bestätigungscode ist ungültig. Bitte versuche es erneut."));
            } else if (error.code === 'auth/code-expired') {
                dispatch(authenticationReducerActions.loginFailure("Der Bestätigungscode ist abgelaufen. Bitte melde dich erneut an."));
            } else {
                dispatch(authenticationReducerActions.loginFailure("Fehler bei der 2-Faktor-Authentifizierung. Bitte versuche es erneut."));
            }
        }
    };
};

export const logout = () => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.logoutRequest());
        dispatch(myOrganizationReducerActions.setProductFruitsChecklists(null));
        dispatch(myOrganizationReducerActions.setProductFruitsUser(null));
        try {
            await userService.logout();
        } catch (error) {
            // We always logout successfully. The server might return an error (e.g. when unauthorized, which means we have been already logged out.
            // In every case we should clear the localStorage, which should then trigger the success case
        }
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Firebase sign-out failed, the Firebase session may still exist in this browser", error);
        }
        // Remove user from local storage to log user out
        localStorage.removeItem("user");
        // Cleared after the logout request, so that request is still part of the session
        clearSession();
        dispatch(authenticationReducerActions.logoutSuccess());
    };
};

export const activateAccount = (externalUUID, newPassword, newPasswordMatch) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.activateUserAccountRequest());
        try {
            const response = await userService.activateAccount(externalUUID, newPassword, newPasswordMatch);
            if (response && response.errorMessage && response.errorMessage.length > 0) {
                dispatch(authenticationReducerActions.activateUserAccountFailure(response.errorMessage));
            } else {
                dispatch(authenticationReducerActions.activateUserAccountSuccess());
                history.push("/login");
            }
        } catch (error) {
            dispatch(authenticationReducerActions.activateUserAccountFailure(error));
        }
    };
};

export const selectOrganizationByLogin = (selectedOrganization, redirectUrl, emailVerificationCode) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.loginRequest(selectedOrganization));
        try {
            const alreadySelected = await userService.keepCurrentOrganizationSelection(selectedOrganization, emailVerificationCode);
            let response = alreadySelected
                ? { selectedOrganization: JSON.parse(localStorage.getItem("user"))?.selectedOrganization || selectedOrganization }
                : await userService.selectOrganization(selectedOrganization, emailVerificationCode);
            // Refresh Firebase token only if shouldUpdateToken is true
            //if (response.shouldUpdateToken) {
                const currentUser = auth.currentUser;
                if (currentUser && !alreadySelected) {
                    try {
                        // Force refresh the token to get updated claims from Firebase
                        const refreshedToken = await currentUser.getIdToken(true);
                         // Get the expiration time from the token claims
                        const tokenResult = await currentUser.getIdTokenResult();
                        const expirationTime = tokenResult.expirationTime;

                        let tokenUser = { 
                            access_token: refreshedToken, 
                            expiration_time: expirationTime,
                            refresh_token: currentUser.refreshToken
                        };

                        userService.mergeUserIntoStorage(tokenUser);
                } catch (tokenRefreshError) {
                        console.error('Failed to refresh Firebase token:', tokenRefreshError);
                        // Continue execution even if token refresh fails
                    }
                }
            //}

            response = userService.mergeUserIntoStorage(response);
            if (redirectUrl && response.selectedOrganization) {
                history.replace(redirectUrl);
            }
            dispatch(authenticationReducerActions.loginSuccess(response));
            trackExistingCustomerLogin(response);
            dispatch(setupActions.getSetupStatus());
            
            return response;
        } catch (error) {
            if (error.message) {
                dispatch(authenticationReducerActions.loginFailure(error.message));
            } else if (error.errorMessage) {
                dispatch(authenticationReducerActions.loginFailure(error.errorMessage));
            } else {
                dispatch(authenticationReducerActions.loginFailure(error));
            }
        }
    };
};

export const reloadUserData = () => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.reloadUserRequest());
        try {
            let user = await userService.reloadUserInStorage();
            dispatch(buildNavigationUpdateAction(user));
            dispatch(authenticationReducerActions.updateLoggedInUserSuccess(user));
            // Fire-and-forget: the VIP badge is cosmetic and its lookup is not part of the main user
            // load (#42857), so it must not delay or fail this request.
            dispatch(fetchVipSupportStatus(user));
        } catch (error) {
            dispatch(authenticationReducerActions.reloadUserFailure(error));
        }
    };
};

/**
 * Companion to reloadUserData (#42857): looks up which of the current user's selected + available
 * organizations have an active VIP subscription and merges the flag into the cached user, without
 * blocking or failing the main user load if it errors or is slow. Takes the just-loaded user
 * explicitly rather than reading it from the store, since the caller may not have committed it yet
 */
export const fetchVipSupportStatus = (user) => {
    return async (dispatch, getState) => {
        const currentUser = user || getState().authentication.user;
        if (!currentUser || !canSeeVipBadges(currentUser)) {
            return;
        }
        const startedForMail = currentUser.mail;
        try {
            const activeOrganizationUUIDs = new Set(await userService.getVipSupportStatus());

            // The fetch above is fire-and-forget from the caller's perspective: a logout, a fresh
            // login, or another reload may have completed while it was in flight. Applying this
            // (now stale) result on top of that would resurrect a logged-out session or clobber
            // newer data, so bail out if the session has moved on.
            const latestState = getState().authentication;
            if (
                !latestState.loggedIn ||
                !latestState.user ||
                latestState.user.mail !== startedForMail ||
                latestState.user.selectedOrganization?.externalUUID !== currentUser.selectedOrganization?.externalUUID
            ) {
                return;
            }

            const latestUser = latestState.user;
            const selectedOrganization = latestUser.selectedOrganization;
            const updatedUser = {
                ...latestUser,
                selectedOrganization: selectedOrganization && typeof selectedOrganization === "object"
                    ? {
                          ...selectedOrganization,
                          hasActiveVipSupport: activeOrganizationUUIDs.has(selectedOrganization.externalUUID),
                      }
                    : selectedOrganization,
                availableOrganizations: Array.isArray(latestUser.availableOrganizations)
                    ? latestUser.availableOrganizations.map((organization) => ({
                          ...organization,
                          hasActiveVipSupport: activeOrganizationUUIDs.has(organization.externalUUID),
                      }))
                    : latestUser.availableOrganizations,
            };
            userService.mergeUserIntoStorage(updatedUser);
            dispatch(authenticationReducerActions.updateLoggedInUserSuccess(updatedUser));
        } catch (error) {
            // Non-critical: the VIP badge simply stays hidden until the next successful lookup.
        }
    };
};

const canSeeVipBadges = (user) => user.userRole === UserRole.SUPER_USER || user.userRole === UserRole.TECHNICAL_USER;

export const resetPassword = (externalUUID, newPassword, newPasswordMatch) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.passwordResetRequest());
        try {
            let response = await userService.resetPassword(externalUUID, newPassword, newPasswordMatch);
            if (response.errorMessage) {
                dispatch(authenticationReducerActions.passwordResetFailure(response.errorMessage));
            } else {
                dispatch(authenticationReducerActions.passwordResetSuccess());
                history.push("/login");
            }
        } catch (error) {
            dispatch(authenticationReducerActions.passwordResetFailure(error));
        }
    };
};

export const setUpResetPassword = (email) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.setupResetPasswordRequest());
        try {
            await userService.setUpResetPassword(email);
            dispatch(authenticationReducerActions.setupResetPasswordSuccess());
            dispatch(authenticationReducerActions.setResetMail(null));
            dispatch(
                authenticationReducerActions.setResetSuccessMessage(
                    "Falls die E-Mail in unserer Datenbank vorhanden ist, werden wir einen Link zum Zurücksetzen an die von dir angegebene E-Mail schicken."
                )
            );
        } catch (error) {
            dispatch(authenticationReducerActions.setupResetPasswordFailure(error));
        }
    };
};

export const sendActivationLink = (mail) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.sendActivationLinkRequest());
        try {
            const response = await userService.sendActivationLink(mail);
            if (response && response.errorMessage) {
                dispatch(authenticationReducerActions.sendActivationLinkFailure(response.errorMessage));
            } else {
                dispatch(authenticationReducerActions.sendActivationLinkSuccess());
            }
        } catch (error) {
            dispatch(authenticationReducerActions.sendActivationLinkFailure(error));
        }
    };
};

export const acceptDocuments = (acceptTos, acceptDpa, authUser, loginUser, redirectUrl) => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.acceptDocumentsRequest());
        try {
            const response = await userService.acceptDocuments(acceptTos, acceptDpa);
            if (response?.errorMessage) {
                dispatch(authenticationReducerActions.acceptDocumentsFailure(response.errorMessage));
            } else {
                // Keep the stored user up to date, selectOrganizationByLogin reads the user back from
                // the storage afterwards and would otherwise restore the outdated accepted documents.
                if (response?.externalUUID) {
                    userService.mergeUserIntoStorage(response);
                }
                dispatch(authenticationReducerActions.acceptDocumentsSuccess(response));
            }
            const { activeTosVersionAccepted, activeDpaVersionAccepted } = useAcceptedDocuments(response);
            if (response?.externalUUID && activeTosVersionAccepted && activeDpaVersionAccepted && loginUser.email && loginUser.password) {
                dispatch(login(loginUser.email, loginUser.password, loginUser.selectedOrganization, redirectUrl));
            } else if (response?.externalUUID && activeTosVersionAccepted && activeDpaVersionAccepted && authUser) {
                // Continue with the response, it carries the freshly accepted documents.
                // The response never contains a selected organization, so keep the one we already had.
                dispatch(
                    resumeLoginWithSSO(
                        { ...response, selectedOrganization: response.selectedOrganization || authUser.selectedOrganization },
                        redirectUrl
                    )
                );
            } else {
                dispatch(
                    authenticationReducerActions.setLoginErrorMessage(
                        "Vor der Anmeldung müssen die Nutzungsvereinbarung und der Vertrag zur Auftragsdatenverarbeitung akzeptiert werden."
                    )
                );
            }
        } catch (error) {
            dispatch(authenticationReducerActions.acceptDocumentsFailure(error));
        }
    };
};

export const checkHubspotSession = () => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.getHubspotSessionRequest());
        try {
            const response = await userService.getHubspotSession();
            dispatch(authenticationReducerActions.getHubspotSessionSuccess(response));
        } catch (error) {
            dispatch(authenticationReducerActions.clearHubspotSessionFailure(error));
        }
    };
};

export const clearHubspotSession = () => {
    return async (dispatch) => {
        dispatch(authenticationReducerActions.clearHubspotSessionRequest());
        try {
            await userService.clearHubspotSession();
            dispatch(authenticationReducerActions.clearHubspotSessionSuccess());
        } catch (error) {
            dispatch(authenticationReducerActions.clearHubspotSessionFailure(error));
        }
    };
};

export const loginClearSSOMethod = () => {
    return (dispatch) => {
        dispatch(authenticationReducerActions.loginClearSSOMethod());
    };
};
